import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './OfferSection.css';

const OfferSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeDetail, setActiveDetail] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [, setActivePanel] = useState(null);
  const detailsPanelRef = useRef(null);
  const actionButtonsRef = useRef([]);

  useEffect(() => {
    setIsVisible(true);
    
    // Add scroll animation for elements - MODIFIED
    const handleScroll = () => {
      const elements = document.querySelectorAll('.offer_action-btn');
      elements.forEach(el => {
        const elementTop = el.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        if (elementTop < windowHeight - 100) {
          el.classList.add('animate');
        } else {
          // Keep the animate class even when out of view temporarily
          // This prevents buttons from hiding when panel opens
          if (!el.classList.contains('force-visible')) {
            el.classList.remove('animate');
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle panel animations
  useEffect(() => {
    if (activeDetail && detailsPanelRef.current) {
      const panel = detailsPanelRef.current;
      panel.classList.add('active');
      
      // Ensure buttons remain visible when panel opens
      actionButtonsRef.current.forEach(btn => {
        if (btn) {
          btn.classList.add('force-visible');
          btn.classList.add('animate');
        }
      });
    } else {
      // When panel closes, remove force-visible but keep animate if in view
      actionButtonsRef.current.forEach(btn => {
        if (btn) {
          btn.classList.remove('force-visible');
          // Check if button is in viewport
          const elementTop = btn.getBoundingClientRect().top;
          const windowHeight = window.innerHeight;
          if (elementTop < windowHeight - 100) {
            btn.classList.add('animate');
          }
        }
      });
    }
  }, [activeDetail]);

  const actionDetails = {
    learn: {
      title: "Free Trial Estimation",
      description: "We offer your first estimation completely free! Experience our quality service with no obligation.",
      steps: [
        "Fill out our simple form below",
        "Provide your project details",
        "Get your first estimation absolutely free",
        "No credit card required, no hidden fees"
      ],
      cta: "Start Your Free Estimation"
    },
    explore: {
      title: "Comprehensive Services",
      description: "Discover our full range of estimation services designed for construction professionals.",
      services: [
        "Lumber & Material Estimation",
        "Flooring & Roofing Calculations",
        "MEP Systems Estimation",
        "Electrical & Plumbing Takeoffs",
        "Painting & Finishing Estimates",
        "Hardware & Openings Calculation"
      ],
      cta: "View All Services"
    },
    process: {
      title: "Our Streamlined Process",
      description: "From submission to delivery, we make estimation simple and transparent.",
      steps: [
        "Create your account in seconds",
        "Login to your personalized dashboard",
        "Upload your project files/blueprints",
        "Our team works with real-time tracking",
        "Receive notifications at each stage",
        "Download your completed estimation"
      ],
      stages: [
        "Step 1: Project Submission",
        "Step 2: Team Assignment",
        "Step 3: Estimation in Progress",
        "Step 4: Quality Review",
        "Step 5: Ready for Delivery"
      ],
      cta: "See Full Process"
    },
    why: {
      title: "Why Choose SuryaGroup?",
      description: "We're not just another estimation service - we're your project partners.",
      benefits: [
        "On-time delivery with ETA notifications",
        "Free live chat support for every project",
        "Transparent pricing with no hidden costs",
        "Experienced estimators with industry expertise",
        "Detailed breakdowns and documentation",
        "24/7 access to your project dashboard"
      ],
      cta: "Learn More About Us"
    }
  };

  const handleActionClick = (action) => {
    if (activeDetail === action) {
      setActiveDetail(null);
      setActivePanel(null);
    } else {
      setActiveDetail(action);
      setActivePanel(action);
      
      // Add animation class to the clicked button
      actionButtonsRef.current.forEach(btn => {
        if (btn && btn.dataset.action === action) {
          btn.classList.add('pulse');
          btn.classList.add('force-visible'); // Keep button visible
          btn.classList.add('animate'); // Ensure animation is applied
          setTimeout(() => {
            btn.classList.remove('pulse');
          }, 600);
        } else {
          // For other buttons, ensure they stay visible too
          btn.classList.add('force-visible');
          btn.classList.add('animate');
        }
      });
    }
  };

  const toggleForm = () => {
    setIsFormVisible(!isFormVisible);
  };

  return (
    <div className="offer_container">
      {/* Animated background elements */}
      <div className="offer_animated-bg">
        <div className="offer_bg-shape offer_bg-shape-1"></div>
        <div className="offer_bg-shape offer_bg-shape-2"></div>
        <div className="offer_bg-shape offer_bg-shape-3"></div>
        <div className="offer_bg-shape offer_bg-shape-4"></div>
      </div>
      
      <div className={`offer_content ${isVisible ? 'offer_fade-in' : ''}`}>
        <div className="offer_header">
          <h2 className="offer_title">
            <span className="offer_title-line">SPEED MEETS</span>
            <span className="offer_title-line offer_accent">ACCURACY</span>
          </h2>
          <p className="offer_subtitle">
            Efficient, Reliable Estimates to Keep Your Projects on Track.
          </p>
          <div className="offer_buttons">
            <button className="offer_btn offer_primary" onClick={toggleForm}>
              <i className="fas fa-blueprint"></i>
              UNLOCK FREE TRIAL SUBSCRIPTION - NOW!
            </button>
            <Link className="offer_btn offer_secondary" to="/schedule-meeting">
              <i className="fas fa-calendar"></i>
              SCHEDULE A MEETING
            </Link>
          </div>
        </div>

        <div className="offer_actions-wrapper">
          <div className="offer_actions">
            <div className="offer_action-item">
              <button 
                className={`offer_action-btn animate ${activeDetail === 'learn' ? 'active' : ''}`}
                onClick={() => handleActionClick('learn')}
                data-action="learn"
                ref={el => actionButtonsRef.current[0] = el}
              >
                <div className="offer_action-icon">
                  <i className="fas fa-book-open"></i>
                </div>
                <span>LEARN MORE</span>
              </button>
            </div>

            <div className="offer_action-item">
              <button 
                className={`offer_action-btn animate ${activeDetail === 'explore' ? 'active' : ''}`}
                onClick={() => handleActionClick('explore')}
                data-action="explore"
                ref={el => actionButtonsRef.current[1] = el}
              >
                <div className="offer_action-icon">
                  <i className="fas fa-cogs"></i>
                </div>
                <span>EXPLORE SERVICES</span>
              </button>
            </div>

            <div className="offer_action-item">
              <button 
                className={`offer_action-btn animate ${activeDetail === 'process' ? 'active' : ''}`}
                onClick={() => handleActionClick('process')}
                data-action="process"
                ref={el => actionButtonsRef.current[2] = el}
              >
                <div className="offer_action-icon">
                  <i className="fas fa-project-diagram"></i>
                </div>
                <span>SEE PROCESS</span>
              </button>
            </div>

            <div className="offer_action-item">
              <button 
                className={`offer_action-btn animate ${activeDetail === 'why' ? 'active' : ''}`}
                onClick={() => handleActionClick('why')}
                data-action="why"
                ref={el => actionButtonsRef.current[3] = el}
              >
                <div className="offer_action-icon">
                  <i className="fas fa-star"></i>
                </div>
                <span>WHY CHOOSE US</span>
              </button>
            </div>
          </div>

          {/* Details Panel */}
          <div className="offer_details-panel" ref={detailsPanelRef}>
            <div className="offer_detail-content">
              {activeDetail === 'learn' && (
                <div className="offer_detail-section slide-in">
                  <h3>{actionDetails.learn.title}</h3>
                  <p>{actionDetails.learn.description}</p>
                  <ul>
                    {actionDetails.learn.steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                  <button className="offer_detail-cta">{actionDetails.learn.cta}</button>
                </div>
              )}
              
              {activeDetail === 'explore' && (
                <div className="offer_detail-section slide-in">
                  <h3>{actionDetails.explore.title}</h3>
                  <p>{actionDetails.explore.description}</p>
                  <div className="offer_services-grid">
                    {actionDetails.explore.services.map((service, index) => (
                      <div key={index} className="offer_service-item">
                        <i className="fas fa-check"></i>
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>
                  <button className="offer_detail-cta">{actionDetails.explore.cta}</button>
                </div>
              )}
              
              {activeDetail === 'process' && (
                <div className="offer_detail-section slide-in">
                  <h3>{actionDetails.process.title}</h3>
                  <p>{actionDetails.process.description}</p>
                  <div className="offer_process-steps">
                    {actionDetails.process.steps.map((step, index) => (
                      <div key={index} className="offer_process-step">
                        <div className="offer_step-number">{index + 1}</div>
                        <div className="offer_step-text">{step}</div>
                      </div>
                    ))}
                  </div>
                  <div className="offer_process-stages">
                    {actionDetails.process.stages.map((stage, index) => (
                      <div key={index} className="offer_process-stage">
                        <i className="fas fa-arrow-right"></i>
                        <span>{stage}</span>
                      </div>
                    ))}
                  </div>
                  <button className="offer_detail-cta">{actionDetails.process.cta}</button>
                </div>
              )}
              
              {activeDetail === 'why' && (
                <div className="offer_detail-section slide-in">
                  <h3>{actionDetails.why.title}</h3>
                  <p>{actionDetails.why.description}</p>
                  <div className="offer_benefits">
                    {actionDetails.why.benefits.map((benefit, index) => (
                      <div key={index} className="offer_benefit">
                        <i className="fas fa-gift"></i>
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                  <button className="offer_detail-cta">{actionDetails.why.cta}</button>
                </div>
              )}
              
              {!activeDetail && (
                <div className="offer_detail-placeholder">
                  <div className="offer_placeholder-icon">
                    <i className="fas fa-hand-pointer"></i>
                  </div>
                  <h3>Explore Our Features</h3>
                  <p>Click on any button to learn more about our services</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferSection;